/**
 * Kit del telefono (7/10/2026): i pezzi con cui sono fatte le pagine "…Mobile".
 * Perché esiste e come si usa: mobile-kit.css e os-planning/mobile.md §12.
 *
 * Regola di fondo: ogni pagina risponde a UNA domanda e la risposta è la prima
 * cosa che si vede (MHero); sotto, pochi gruppi (MGroup) di righe (MRow); il
 * resto si apre toccando. Il computer non disegna mai questi componenti.
 */
import type { ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { MobileSheet } from '../ui/MobileSheet';
import './mobile-kit.css';

/**
 * I nomi scritti tutti in maiuscolo ("EMMEQUADRO INGEGNERI S.R.L.") su 390px
 * occupano quasi il doppio e si tagliano prima. Solo per la riga del telefono
 * diventano "Emmequadro Ingegneri S.r.l."; il dato salvato non cambia.
 */
export function leggibile(nome: string): string {
  if (nome.length < 5 || nome !== nome.toUpperCase() || !/[A-Z]{3}/.test(nome)) return nome;
  return nome
    .toLowerCase()
    .replace(/\b(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?)(?=\s|$)/g, (m) => m.toUpperCase().replace(/R\.L/g, 'r.l').replace(/P\.A/g, 'p.A'))
    .replace(/(^|[\s(/'-])([a-zà-ù])/g, (_m, p: string, c: string) => p + c.toUpperCase())
    // "di", "e", "della" minuscoli in mezzo al nome, non all'inizio ("Di Stefano" resta così).
    .replace(/(?<=\s)(Di|Del|Della|Dei|E|Ed|Da|In)(?=\s)/g, (m) => m.toLowerCase());
}

/** Le iniziali per il cerchio di una riga: "New Omnia" → "NO", "GVF S.r.l." → "GV", "Fausto Carbone" → "FC". */
export function iniziali(nome: string): string {
  const parole = nome
    .replace(/\b(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?|s\.?s\.?)(?=\s|$|\))/gi, ' ')
    .replace(/\b(di|e|il|la|lo|gli|le|del|della|dei)\b/gi, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!parole.length) return '·';
  const due = parole.length === 1 ? parole[0]!.slice(0, 2) : parole[0]!.charAt(0) + parole[1]!.charAt(0);
  return due.toUpperCase();
}

export function MPage({ children }: { children: ReactNode }) {
  return <div className="mk">{children}</div>;
}

/** Il saluto della home: nome e data, niente altro. */
export function MHello({ nome, sotto }: { nome?: string; sotto: ReactNode }) {
  return (
    <div className="mk-hello">
      <h1>Ciao{nome ? <>, <strong>{nome}</strong></> : null}.</h1>
      <div className="mk-hello-sub">{sotto}</div>
    </div>
  );
}

/**
 * La risposta della pagina: un'etichetta che dice di cosa si parla, il numero
 * grande, una riga sotto. Se si tocca, porta dove si spiega.
 */
export function MHero({ label, value, unit, sub, alert, onClick }: {
  label: ReactNode;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  alert?: boolean;
  onClick?: () => void;
}) {
  const corpo = (
    <>
      <div className="mk-hero-label">{label}{onClick && <Icon name="chevronR" size={11} className="mk-hero-chev" />}</div>
      <div className="mk-hero-value">{unit && <span className="u">{unit}</span>}{value}</div>
      {sub && <div className="mk-hero-sub">{sub}</div>}
    </>
  );
  const cls = `mk-hero${alert ? ' is-alert' : ''}`;
  return onClick ? <button type="button" className={cls} onClick={onClick}>{corpo}</button> : <div className={cls}>{corpo}</div>;
}

/** Un gruppo: titolo normale (non maiuscole spaziate), conteggio, un comando a destra, poi la lista. */
export function MGroup({ title, count, action, note, children }: {
  title: ReactNode;
  count?: number;
  action?: { label: string; onClick: () => void };
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mk-group-hd">
        <h2 className="mk-group-title">
          {title}
          {count != null && <span className="mk-group-count">{count}</span>}
        </h2>
        {action && (
          <button type="button" className="mk-group-act" onClick={action.onClick}>
            {action.label} <Icon name="chevronR" size={11} />
          </button>
        )}
      </div>
      <div className="mk-list">{children}</div>
      {note && <div className="mk-group-note">{note}</div>}
    </section>
  );
}

export type MLeadTone = 'alert' | 'ink' | undefined;

/**
 * Una riga, come quelle delle impostazioni dell'iPhone: icona (o iniziali),
 * titolo, una riga di dettaglio, valore a destra, freccia se si apre.
 */
export function MRow({ icon, text, tone, round, title, sub, subWrap, trail, trailSub, trailTone, onClick, chevron }: {
  icon?: IconName;
  /** Al posto dell'icona: iniziali o una data corta. */
  text?: string;
  tone?: MLeadTone;
  round?: boolean;
  title: ReactNode;
  sub?: ReactNode;
  subWrap?: boolean;
  trail?: ReactNode;
  trailSub?: ReactNode;
  trailTone?: 'alert' | 'mute';
  onClick?: () => void;
  chevron?: boolean;
}) {
  const lead = icon || text;
  const corpo = (
    <>
      {lead && (
        <span className={`mk-lead${tone ? ` is-${tone}` : ''}${text ? ' is-text' : ''}${round ? ' is-round' : ''}`} aria-hidden="true">
          {icon ? <Icon name={icon} size={16} /> : text}
        </span>
      )}
      <span className="mk-main">
        <span className="mk-title">{title}</span>
        {sub && <span className={`mk-sub${subWrap ? ' is-wrap' : ''}`}>{sub}</span>}
      </span>
      {trail != null && (
        <span className={`mk-trail${trailTone ? ` is-${trailTone}` : ''}`}>
          {trail}
          {trailSub && <span className="mk-trail-sub">{trailSub}</span>}
        </span>
      )}
      {(chevron ?? !!onClick) && <Icon name="chevronR" size={13} className="mk-chev" />}
    </>
  );
  const cls = `mk-row${lead ? ' has-lead' : ''}`;
  return onClick
    ? <button type="button" className={cls} onClick={onClick}>{corpo}</button>
    : <div className={cls}>{corpo}</div>;
}

/**
 * L'intestazione di una scheda di dettaglio: iniziali grandi, nome, una riga
 * che dice cos'è. Come la scheda di un contatto dell'iPhone. Il "‹" per tornare
 * indietro e il tipo di scheda li mette già la barra in alto.
 */
export function MIdentity({ text, icon, title, sub, extra }: {
  text?: string;
  icon?: IconName;
  title: ReactNode;
  sub?: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="mk-id">
      {(text || icon) && <span className="mk-id-av" aria-hidden="true">{icon ? <Icon name={icon} size={26} /> : text}</span>}
      <h1 className="mk-id-title">{title}</h1>
      {sub && <div className="mk-id-sub">{sub}</div>}
      {extra}
    </div>
  );
}

/** Il titolo grande di una scheda (task, lavoro): a sinistra, si tocca per modificarlo se si può. */
export function MHead({ title, sub, done, onClick }: { title: ReactNode; sub?: ReactNode; done?: boolean; onClick?: () => void }) {
  const h = <h1 className={`mk-head-title${done ? ' is-done' : ''}`}>{title}</h1>;
  return (
    <div className="mk-head">
      {onClick ? <button type="button" className="mk-head-btn" onClick={onClick}>{h}</button> : h}
      {sub && <div className="mk-head-sub">{sub}</div>}
    </div>
  );
}

export interface MAzione {
  label: string;
  icon: IconName;
  /** Un link (tel:, mailto:, https://wa.me/…) oppure un'azione. Senza nessuno dei due il bottone è spento. */
  href?: string | null;
  onClick?: () => void;
}

/** La fila dei gesti rapidi sotto l'intestazione: chiama, scrivi, WhatsApp, mappa… */
export function MActions({ azioni }: { azioni: MAzione[] }) {
  return (
    <div className="mk-acts">
      {azioni.map((a) => {
        const corpo = <><span className="mk-act-ico"><Icon name={a.icon} size={18} /></span><span className="mk-act-lbl">{a.label}</span></>;
        if (a.href) {
          const esterno = /^https?:/.test(a.href);
          return <a key={a.label} className="mk-act" href={a.href} {...(esterno ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{corpo}</a>;
        }
        return <button key={a.label} type="button" className="mk-act" disabled={!a.onClick} onClick={a.onClick}>{corpo}</button>;
      })}
    </div>
  );
}

/** Un elenco di scelte che sale dal basso: tocchi la riga e salva (la spunta segna quella attuale). */
export function MScelte({ open, onClose, titolo, nota, opzioni, valore, onScegli }: {
  open: boolean;
  onClose: () => void;
  titolo: string;
  nota?: string;
  opzioni: Array<{ v: string; l: string; sub?: string }>;
  valore: string;
  onScegli: (v: string) => void;
}) {
  return (
    <MobileSheet open={open} onClose={onClose} title={titolo}>
      {nota && <div className="mk-group-note mk-sheet-note">{nota}</div>}
      <div className="mk-list">
        {opzioni.map((o) => (
          <MRow key={o.v || 'vuoto'} title={o.l} sub={o.sub} trail={o.v === valore ? <Icon name="check" size={16} /> : undefined} chevron={false} onClick={() => onScegli(o.v)} />
        ))}
      </div>
    </MobileSheet>
  );
}

/** Un campo solo che sale dal basso, con Salva e (se c'è già un valore) il bottone per toglierlo. */
export function MCampo({ open, onClose, titolo, nota, campo, onSalva, onTogli, togli }: {
  open: boolean;
  onClose: () => void;
  titolo: string;
  nota?: string;
  campo: ReactNode;
  onSalva: () => void;
  onTogli?: () => void;
  togli?: string;
}) {
  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={titolo}
      footer={
        <div className="mk-sheet-ft">
          <button type="button" className="btn btn-primary m-block" onClick={onSalva}>Salva</button>
          {onTogli && <button type="button" className="btn m-block" onClick={onTogli}>{togli ?? 'Togli'}</button>}
        </div>
      }
    >
      {nota && <div className="mk-group-note mk-sheet-note">{nota}</div>}
      <div className="mk-sheet-field">{campo}</div>
    </MobileSheet>
  );
}

export function MEmpty({ children }: { children: ReactNode }) {
  return <div className="mk-empty">{children}</div>;
}

/** "Vedi tutte le 12": in fondo al gruppo, come ultima riga. */
export function MMore({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="mk-more" onClick={onClick}>
      {label} <Icon name="chevronD" size={11} />
    </button>
  );
}

/** Le viste di una pagina: pillole che scorrono, quella accesa è piena. */
export function MSeg<K extends string>({ value, options, onChange }: {
  value: K;
  options: Array<{ key: K; label: string; count?: number }>;
  onChange: (k: K) => void;
}) {
  return (
    <div className="mk-seg" role="tablist">
      {options.map((o) => (
        <button key={o.key} type="button" role="tab" aria-pressed={value === o.key} aria-selected={value === o.key} onClick={() => onChange(o.key)}>
          {o.label}
          {o.count != null && <span className="n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function MSearch({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="mk-search">
      <Icon name="search" size={14} />
      <input type="search" inputMode="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} aria-label={placeholder} />
    </label>
  );
}
